import { useState, useCallback, useEffect } from 'react';

export interface ColumnDefinition {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  visible?: boolean;
}

export interface ColumnConfig {
  id: string;
  visible: boolean;
  order: number;
}

interface UseColumnConfigOptions {
  storageKey: string;
  defaultColumns: ColumnDefinition[];
}

interface UseColumnConfigReturn {
  columns: ColumnDefinition[];
  visibleColumns: ColumnDefinition[];
  columnConfigs: ColumnConfig[];
  reorderColumns: (activeId: string, overId: string) => void;
  toggleVisibility: (columnId: string) => void;
  setVisibility: (columnId: string, visible: boolean) => void;
  resetToDefaults: () => void;
  isDefaultOrder: boolean;
}

export function useColumnConfig({ storageKey, defaultColumns }: UseColumnConfigOptions): UseColumnConfigReturn {
  const getDefaultConfigs = (): ColumnConfig[] => {
    return defaultColumns.map((col, index) => ({
      id: col.id,
      visible: col.visible !== false,
      order: index,
    }));
  };

  const loadFromStorage = (): ColumnConfig[] => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as ColumnConfig[];
        // Merge with defaults to handle new columns
        const existingIds = new Set(parsed.map(c => c.id));
        const merged = [...parsed];

        // Add any new columns that weren't in storage
        defaultColumns.forEach((col, index) => {
          if (!existingIds.has(col.id)) {
            merged.push({
              id: col.id,
              visible: col.visible !== false,
              order: merged.length,
            });
          }
        });

        // Remove columns that no longer exist in defaults
        const validIds = new Set(defaultColumns.map(c => c.id));
        return merged.filter(c => validIds.has(c.id));
      }
    } catch (e) {
      console.warn('Failed to load column config from storage:', e);
    }
    return getDefaultConfigs();
  };

  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>(loadFromStorage);

  // Save to storage when configs change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnConfigs));
    } catch (e) {
      console.warn('Failed to save column config to storage:', e);
    }
  }, [columnConfigs, storageKey]);

  // Get sorted columns based on order
  const columns = [...defaultColumns].sort((a, b) => {
    const configA = columnConfigs.find(c => c.id === a.id);
    const configB = columnConfigs.find(c => c.id === b.id);
    return (configA?.order ?? 0) - (configB?.order ?? 0);
  });

  // Get only visible columns
  const visibleColumns = columns.filter(col => {
    const config = columnConfigs.find(c => c.id === col.id);
    return config?.visible !== false;
  });

  const reorderColumns = useCallback((activeId: string, overId: string) => {
    setColumnConfigs(prev => {
      const oldIndex = prev.findIndex(c => c.id === activeId);
      const newIndex = prev.findIndex(c => c.id === overId);

      if (oldIndex === -1 || newIndex === -1) return prev;

      const newConfigs = [...prev];
      const [removed] = newConfigs.splice(oldIndex, 1);
      newConfigs.splice(newIndex, 0, removed);

      // Update order values
      return newConfigs.map((config, index) => ({
        ...config,
        order: index,
      }));
    });
  }, []);

  const toggleVisibility = useCallback((columnId: string) => {
    setColumnConfigs(prev =>
      prev.map(config =>
        config.id === columnId
          ? { ...config, visible: !config.visible }
          : config
      )
    );
  }, []);

  const setVisibility = useCallback((columnId: string, visible: boolean) => {
    setColumnConfigs(prev =>
      prev.map(config =>
        config.id === columnId
          ? { ...config, visible }
          : config
      )
    );
  }, []);

  const resetToDefaults = useCallback(() => {
    const defaults = getDefaultConfigs();
    setColumnConfigs(defaults);
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  // Check if order matches defaults
  const isDefaultOrder = columnConfigs.every((config, index) => {
    const defaultConfig = getDefaultConfigs()[index];
    return config.id === defaultConfig?.id && config.visible === defaultConfig?.visible;
  });

  return {
    columns,
    visibleColumns,
    columnConfigs,
    reorderColumns,
    toggleVisibility,
    setVisibility,
    resetToDefaults,
    isDefaultOrder,
  };
}

export default useColumnConfig;
